// The auth client: one function per destination spine endpoint. Measured
// fixture behavior remains pinned by authApi.test.ts. Password reset is the
// already-governed D26 UUID protocol; this client only submits the existing
// shapes and never owns issuance, redemption, or token storage.
import { authRequest } from "./transport";
import type {
  AccessRequestCreateResponse,
  AuthUser,
  ChangePasswordResponse,
  LoginResponse,
  PasswordResetConfirmResponse,
  PasswordResetRequestResponse,
  RefreshResponse,
  ResendVerificationResponse,
  VerifyEmailResponse,
} from "./types";

export const authApi = {
  // `email` is the wire key VIP's measured request uses; since contract v2.23
  // the server accepts a USERNAME there too, which is how ZW users sign in.
  // The key keeps VIP's spelling rather than diverging the contract from the
  // corpus for a naming aesthetic.
  //
  // `termsAccepted` (v2.24) is ZW's consent step. Omitted entirely for VIP so
  // their request shape is byte-identical to the measured one, and sent only
  // when the user actually ticked it — consent is never inferred.
  login(
    email: string,
    password: string,
    termsAccepted?: boolean,
  ): Promise<LoginResponse> {
    return authRequest<LoginResponse>("login/", {
      method: "POST",
      body:
        termsAccepted === undefined
          ? { email, password }
          : { email, password, terms_accepted: termsAccepted },
    });
  },

  // D3: the refresh token rides the httpOnly cookie (the spine reads it
  // server-side), so the body is an empty object — unlike the measured
  // scopely request, where the SPA still held the token in JS. The 205 /
  // 400-missing-refresh response contract is unchanged.
  logout(): Promise<void> {
    return authRequest<void>("logout/", { method: "POST", body: {} });
  },

  me(): Promise<AuthUser> {
    return authRequest<AuthUser>("me/");
  },

  // D3: same cookie-borne refresh token note as logout().
  refresh(): Promise<RefreshResponse> {
    return authRequest<RefreshResponse>("refresh/", {
      method: "POST",
      body: {},
    });
  },

  /**
   * The signed-in user's own profile record.
   *
   * MEASURED: contract-fixtures/scopely/orgs/auth-profile/success.json
   * (manifest slug `auth-profile`, GET /api/v1/auth/profile/, captured
   * 2026-08-06 with the note "dashboard-api fetchProfile()"). Its body is
   * BYTE-IDENTICAL to the measured auth/me body for the same user — same
   * twelve keys, same values — which is why typing it as AuthUser is a
   * measured claim rather than an assumption.
   *
   * NOT ON THE SPINE. src/shared/api/generated.ts lists seven
   * /api/spine/v1/auth/* paths and profile/ is not among them, so this
   * call 404s against the real backend today and resolves only against
   * `npm run mock` (the mock server indexes the manifest generically, and
   * the manifest carries this case). Named backend row; the screen
   * surfaces the failure rather than rendering blank fields as if they
   * were the user's data.
   */
  profile(): Promise<AuthUser> {
    return authRequest<AuthUser>("profile/");
  },

  /**
   * Update the signed-in user's own profile.
   *
   * PARTLY UNMEASURED, stated plainly — same posture as the team module's
   * createTeamMember. The VERB and PATH are measured (the GET's response
   * records `allow: GET, PATCH, HEAD, OPTIONS`); the REQUEST body is a
   * port of the reference client (lib/dashboard-api.ts updateProfile),
   * not captured behavior; and the RESPONSE body is unmeasured — the
   * reference types it as the updated AuthUser and this follows it,
   * because the GET of the same resource is measured as exactly that.
   *
   * No environment serves it: not on the spine (above), and the mock
   * server has no PATCH case to resolve. So Save Changes fails loudly
   * into the screen's alert instead of appearing to succeed. Capture a
   * fixture before relying on response details.
   */
  updateProfile(data: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string | null;
  }): Promise<AuthUser> {
    return authRequest<AuthUser>("profile/", { method: "PATCH", body: data });
  },

  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<ChangePasswordResponse> {
    return authRequest<ChangePasswordResponse>("change-password/", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    });
  },

  // S7: the reference's public Request Access submission, live against the
  // spine's contract v2.15 endpoint (b-lane-public-signup-endpoint). Success
  // is the uniform { message }; duplicate account / already-pending refusals
  // surface as 400s with the reference's own copy (the Protocol carries the
  // louder-than-anti-oracle rationale).
  requestAccess(data: {
    first_name: string;
    last_name: string;
    email: string;
    company_name: string;
    company_url: string;
    source?: "web" | "qr";
  }): Promise<AccessRequestCreateResponse> {
    return authRequest<AccessRequestCreateResponse>("access-requests/", {
      method: "POST",
      body: data,
    });
  },

  // v2.16: self-serve re-issue of a verification link — the recovery path
  // for an expired or spam-eaten link. Strictly anti-oracle: the server
  // returns one uniform { message } for every outcome, so the client must
  // not infer whether anything was pending.
  resendVerification(email: string): Promise<ResendVerificationResponse> {
    return authRequest<ResendVerificationResponse>("resend-verification/", {
      method: "POST",
      body: { email },
    });
  },

  // D26: server-owned recovery issuance returns the same neutral message for
  // existing and unknown addresses. The client must not infer account state.
  requestPasswordReset(email: string): Promise<PasswordResetRequestResponse> {
    return authRequest<PasswordResetRequestResponse>(
      "reset-password/request/",
      {
        method: "POST",
        body: { email },
      },
    );
  },

  // D26: completion accepts only the existing UUID token and new password
  // pair. The token stays request-scoped; no JS-readable credential store or
  // uid/Django-token translation is introduced here.
  completePasswordReset(
    token: string,
    newPassword: string,
    newPasswordConfirm: string,
  ): Promise<PasswordResetConfirmResponse> {
    return authRequest<PasswordResetConfirmResponse>("reset-password/", {
      method: "POST",
      body: {
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      },
    });
  },

  // Contract v2.14: single-use 24h UUID link redemption — a deliberate
  // deviation from the reference's {email, code} pair (its 6-digit mailed
  // code has no counterpart here; the Protocol docstring carries the
  // rationale). Redemption is account-creating, so like reset it is
  // anonymous and the token stays request-scoped.
  verifyEmail(token: string): Promise<VerifyEmailResponse> {
    return authRequest<VerifyEmailResponse>("verify-email/", {
      method: "POST",
      body: { token },
    });
  },
};
